import type { BaseQueryMeta } from '@reduxjs/toolkit/query';
import type { TypeOf, ZodType } from 'zod';

/**
 * Validate Factory (useful for rtk-query validation)
 * Will Drop the response if validation fails.
 */
export function validateStrict<T extends ZodType>(
    schema: T
): (response: unknown, meta: unknown, arg: unknown) => TypeOf<T> {
    return response => schema.parse(response);
}

/**
 * Validate Factory (useful for rtk-query validation)
 * Will Log an error if validation fails, but will not drop the response.
 */
export function validateWarn<T extends ZodType>(
    schema: T // biome-ignore lint/suspicious/noExplicitAny: fine here
): (response: unknown, meta: BaseQueryMeta<any>, arg: unknown) => TypeOf<T> {
    return (response, meta) => {
        const { error } = schema.safeParse(response);
        if (error) {
            console.error(`[feature/api-validation] ${getDebugUrl(meta)} error:`, error.issues);
        }
        return response;
    };
}

/**
 * Validates array entries independently and drops only invalid entries.
 * A non-array response still fails because the response container itself is invalid.
 */
export function validateArrayItems<T extends ZodType>(
    schema: T // biome-ignore lint/suspicious/noExplicitAny: fine here
): (response: unknown, meta: BaseQueryMeta<any>, arg: unknown) => TypeOf<T>[] {
    return (response, meta) => {
        if (!Array.isArray(response)) {
            return schema.array().parse(response);
        }

        return response.flatMap((item, index) => {
            const result = schema.safeParse(item);
            if (result.success) {
                return [result.data];
            }

            const itemId =
                typeof item === 'object' && item !== null && 'id' in item && typeof item.id === 'string'
                    ? ` (${item.id})`
                    : '';
            console.error(
                `[feature/api-validation] ${getDebugUrl(meta)} item ${index}${itemId} error:`,
                result.error.issues
            );
            return [];
        });
    };
}

// biome-ignore lint/suspicious/noExplicitAny: fine here
const getDebugUrl = (meta: BaseQueryMeta<any>) => {
    const url = meta?.request?.url;
    if (!url) {
        return 'unknown';
    }
    return url.replace(new URL(url).origin, '');
};
